import React, { useRef, useEffect, useState, useCallback } from 'react';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import DateDivider from './DateDivider';
import { debugLog } from '../../utils/debug';

const MessageList = ({ messages, isTyping, error, user, onNavigateToSchedule, activeTab, setToastMessage, onSendMessage, promptInfo, onboardingData, isLoadingOnboardingMessages }) => {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [displayedMessageCount, setDisplayedMessageCount] = useState(30);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const scrollTimeoutRef = useRef(null);

  // No longer needed - MessageInput is now part of flex layout, so scrollable area automatically accounts for it

  // Reset displayed count when switching conversations or when messages are cleared
  useEffect(() => {
    if (messages.length < lastMessageCount || messages.length === 0) {
      setDisplayedMessageCount(30);
      setShouldScrollToBottom(true); // Flag that we need to scroll to bottom
      setHasNewMessages(false); // Reset new messages indicator
    } else if (messages.length > lastMessageCount && showJumpToBottom) {
      // New messages arrived while user is scrolled up
      setHasNewMessages(true);
    }
    setLastMessageCount(messages.length);
  }, [messages.length, lastMessageCount, showJumpToBottom]);

  // Get the messages to display (last N messages)
  const displayedMessages = messages.slice(-displayedMessageCount);
  const hasMoreMessages = messages.length > displayedMessageCount;

  // Force scroll to bottom function
  const forceScrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      // Multiple methods to ensure scrolling works
      const container = messagesContainerRef.current;
      
      // Method 1: Immediate scroll
      container.scrollTop = container.scrollHeight;
      
      // Method 2: RequestAnimationFrame for DOM updates
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
        
        // Method 3: scrollIntoView as final backup
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
        }
        
        // Method 4: Final scroll after a small delay
        setTimeout(() => {
          container.scrollTop = container.scrollHeight;
        }, 100);
      });
    }
  }, []);

  // Load more messages when scrolling near the top
  const handleScroll = useCallback((e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Clear any pending scroll timeout to avoid conflicts
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    
    // Check if we should show the jump to bottom button
    // Show button when user is more than 150px from the bottom
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const shouldShow = distanceFromBottom > 150;
    
    
    setShowJumpToBottom(shouldShow);
    
    // If scrolled to within 50px of the top and there are more messages
    if (scrollTop < 50 && hasMoreMessages && !isLoadingMore) {
      setIsLoadingMore(true);
      
      // Store current scroll position to maintain it after loading
      const previousScrollHeight = scrollHeight;
      const previousScrollTop = scrollTop;
      
      // Use requestAnimationFrame for smoother updates
      requestAnimationFrame(() => {
        // Load 20 more messages
        const newCount = Math.min(displayedMessageCount + 20, messages.length);
        setDisplayedMessageCount(newCount);
        
        // Restore scroll position after DOM update
        requestAnimationFrame(() => {
          if (messagesContainerRef.current) {
            const newScrollHeight = messagesContainerRef.current.scrollHeight;
            const scrollDifference = newScrollHeight - previousScrollHeight;
            messagesContainerRef.current.scrollTop = previousScrollTop + scrollDifference;
          }
          setIsLoadingMore(false);
        });
      });
    }
  }, [hasMoreMessages, isLoadingMore, displayedMessageCount, messages.length]);

  // Handle forced scroll to bottom (for conversation switches)
  useEffect(() => {
    if (shouldScrollToBottom) {
      forceScrollToBottom();
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom, forceScrollToBottom, displayedMessages.length]);

  // Scroll to bottom when new messages arrive or when typing status changes
  useEffect(() => {
    // Don't auto-scroll if we're in the middle of a forced scroll
    if (shouldScrollToBottom) return;
    
    // Auto-scroll for new messages - be more aggressive about scrolling
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 200; // Increased threshold
      
      // Always scroll for new messages, regardless of position
      const shouldScroll = messages.length > lastMessageCount || isTyping;
      
      if (shouldScroll || isNearBottom) {
        // Use multiple methods to ensure scrolling works
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }
          // Backup method
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        }, 50);
      }
    }
  }, [messages, isTyping, shouldScrollToBottom, lastMessageCount]);

  // Additional effect to ensure scroll to bottom when messages first load in a conversation
  useEffect(() => {
    if (displayedMessageCount === 30 && messages.length > 0 && displayedMessages.length > 0) {
      // Small delay to ensure DOM is ready, then force scroll
      setTimeout(() => {
        setShouldScrollToBottom(true);
      }, 50);
    }
  }, [displayedMessageCount, messages.length, displayedMessages.length]);

  // Aggressive scroll for new messages - trigger immediately when message count increases
  useEffect(() => {
    if (messages.length > 0 && messages.length > lastMessageCount) {
      // Force scroll to bottom for any new message
      setTimeout(() => {
        forceScrollToBottom();
      }, 100);
    }
  }, [messages.length, lastMessageCount, forceScrollToBottom]);

  // Scroll to bottom when container shrinks (e.g. keyboard opens on iOS)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    let prevHeight = container.clientHeight;
    const observer = new ResizeObserver((entries) => {
      const newHeight = entries[0].contentRect.height;
      if (newHeight < prevHeight) {
        setTimeout(() => forceScrollToBottom(), 280);
      }
      prevHeight = newHeight;
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [forceScrollToBottom]);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Handle jump to bottom button click
  const handleJumpToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
      
      // Hide the button after scrolling starts
      setShowJumpToBottom(false);
      setHasNewMessages(false); // Clear new messages indicator
      
      // Re-check scroll position after animation completes
      setTimeout(() => {
        if (messagesContainerRef.current) {
          const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
          const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
          setShowJumpToBottom(distanceFromBottom > 150);
        }
      }, 500);
    }
  }, []);

  return (
    <>
        <div 
          ref={messagesContainerRef}
          className="overflow-y-auto overflow-x-hidden px-4 py-2 md:px-6 md:py-3 bg-gray-900 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800"
          onScroll={handleScroll}
          style={{ 
            height: '100%',
            scrollBehavior: 'auto'
          }}
        >
      {error && (
        <div className="bg-red-900/20 border border-red-600/50 text-red-200 px-4 py-3 rounded-xl mb-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-red-800/30 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-red-400 text-sm">!</span>
            </div>
            <div>
              <strong className="font-medium text-red-300">Error:</strong>
              <span className="block mt-1 text-red-200/90">{error}</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Load more indicator */}
      {hasMoreMessages && (
        <div className="text-center py-3">
          {isLoadingMore ? (
            <div className="flex items-center justify-center gap-2 text-gray-400">
              <div className="animate-spin w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full"></div>
              <span className="text-xs">Loading...</span>
            </div>
          ) : (
            <div className="text-xs text-gray-500 opacity-75">
              Scroll up for older messages ({messages.length - displayedMessageCount} more)
            </div>
          )}
        </div>
      )}
      
      {messages.length === 0 && !error ? (
        <div className="flex flex-col items-center justify-center h-full px-6 text-center">
          {isLoadingOnboardingMessages ? (
            <div className="text-gray-400">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400 mx-auto mb-3"></div>
              <p className="text-sm">Setting up your personalized coaching…</p>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-white mb-1.5">Ask your coach anything</h2>
              <p className="text-sm text-gray-400 mb-6">Tap a suggestion or type your own question.</p>
              <div className="flex flex-col gap-2 w-full max-w-sm">
                {[
                  'How should I pace my long run this week?',
                  "What's a good warm-up for intervals?",
                  'How do I avoid shin splints?',
                ].map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => onSendMessage(prompt)}
                    className="text-left text-sm text-gray-200 bg-gray-800/60 hover:bg-gray-700/70 active:bg-gray-700 border border-gray-700/60 rounded-2xl px-4 py-3 transition-colors active:scale-[0.99]"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <div>
          {displayedMessages.map((message, index) => {
            // Show date divider on first message and whenever the day changes
            const prev = index > 0 ? displayedMessages[index - 1] : null;
            const next = index + 1 < displayedMessages.length ? displayedMessages[index + 1] : null;
            const sameDay = (a, b) => {
              if (!a || !b) return false;
              const da = new Date(a), db = new Date(b);
              return da.getFullYear() === db.getFullYear() &&
                da.getMonth() === db.getMonth() &&
                da.getDate() === db.getDate();
            };
            const showDateDivider = index === 0 || !sameDay(message.created_at, prev?.created_at);

            // Cluster: same sender within 60 seconds = tighter spacing, no avatar repeat
            const sameSenderAsPrev = !!prev &&
              prev.role === message.role &&
              !showDateDivider &&
              Math.abs(new Date(message.created_at).getTime() - new Date(prev.created_at).getTime()) < 60000;
            const sameSenderAsNext = !!next &&
              next.role === message.role &&
              sameDay(message.created_at, next.created_at) &&
              Math.abs(new Date(next.created_at).getTime() - new Date(message.created_at).getTime()) < 60000;

            // Use tempId for key if it exists (for temp messages), otherwise use regular id
            // This prevents React from unmounting/remounting when temp message is replaced
            const messageKey = message.tempId || message.id;

            return (
              <React.Fragment key={messageKey}>
                {showDateDivider && (
                  <DateDivider date={message.created_at} />
                )}
                {message.is_plan_button ? (
                  <div className="flex flex-col sm:flex-row justify-center items-stretch gap-3 my-4 w-full sm:w-auto px-4 sm:px-0">
                    {/* Adjust Plan Button */}
                    <button
                      onClick={() => {
                        debugLog('💬 [PLAN-BUTTON] User clicked Adjust Plan button');
                        onSendMessage("I'd like to adjust this training plan", { 
                          adjust_plan: true,
                          plan_metadata: message.plan_data 
                        });
                      }}
                      className="w-full sm:w-auto px-6 py-3 bg-gray-700 hover:bg-gray-600 active:bg-gray-700/90 text-gray-200 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 active:scale-[0.98]"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Adjust Plan
                    </button>
                    
                    {/* Create Full Schedule Button */}
                    <button
                      onClick={() => {
                        debugLog('🎯 [PLAN-BUTTON] User clicked Create Full Schedule button');
                        // Include plan metadata in the message for backend to parse (hidden metadata marker)
                        const planMetadata = `[PLAN_META: ${message.plan_data?.weeks || 0} weeks, ${message.plan_data?.start_date || ''} to ${message.plan_data?.end_date || ''}]`;
                        onSendMessage(`Yes, create the full ${message.plan_data?.weeks || 0}-week schedule now. ${planMetadata}`, { 
                          execute_plan: true,
                          plan_metadata: message.plan_data 
                        });
                      }}
                      className="w-full sm:w-auto px-6 py-3 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white rounded-xl font-medium shadow-md transition-colors flex items-center justify-center gap-2 active:scale-[0.98]"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Create Full Schedule
                      {message.plan_data?.weeks && (
                        <span className="text-xs opacity-80">
                          ({message.plan_data.weeks} weeks)
                        </span>
                      )}
                    </button>
                  </div>
                ) : (
                  <MessageBubble
                    message={message}
                    index={index}
                    messages={messages}
                    user={user}
                    onNavigateToSchedule={onNavigateToSchedule}
                    setToastMessage={setToastMessage}
                    promptInfo={promptInfo}
                    onSendMessage={onSendMessage}
                    onboardingData={onboardingData}
                    sameSenderAsPrev={sameSenderAsPrev}
                    sameSenderAsNext={sameSenderAsNext}
                  />
                )}
              </React.Fragment>
            );
          })}
          
          {/* Typing indicator */}
          {isTyping && <TypingIndicator promptInfo={promptInfo} />}
          
          <div ref={messagesEndRef} className="h-1" />
        </div>
      )}
      
      {/* Scroll to bottom button — proper React component, 44pt tap target */}
      {showJumpToBottom && activeTab === 'chat' && (
        <button
          onClick={handleJumpToBottom}
          aria-label="Jump to latest"
          className={`fixed right-4 w-11 h-11 rounded-full flex items-center justify-center z-40 shadow-lg backdrop-blur-sm transition-colors active:scale-95 ${
            hasNewMessages
              ? 'bg-blue-500/90 hover:bg-blue-600/90 text-white'
              : 'bg-gray-700/90 hover:bg-gray-600/90 text-gray-200'
          }`}
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)' }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </button>
      )}

      </div>
    </>
  );
};

export default MessageList; 